<script lang="ts">
  import { location } from 'svelte-spa-router';
  import { showToast } from '../lib/toast';
  import { _ } from 'svelte-i18n';
  import { getMatch, updateMatchHighlights, trackEvent } from '../lib/apiService';
  import { settingsStore, refreshSettings } from '../stores/settings';
  import ShareView from '../components/ShareView.svelte';
  import { saveReportWithImage, formatDateShort } from '../lib/utils/reportUtils';
  import { onMount } from 'svelte';
  // Import view components
  import MatchPreparation from '../components/match/MatchPreparation.svelte';
  import MatchHighlights from '../components/match/MatchHighlights.svelte';
  import MatchReport from '../components/match/MatchReport.svelte';

  // We ignore the passed parameters from the router, and use the ones from the URL to support changes in the URL
  // TODO: how to handle this in a better way?
  $: matchParams = $location.split('/').filter(Boolean);
  $: matchId = matchParams[1] || null;
  $: view = matchParams[2] || 'details'; // Default to details view
  
  // Match data
  let match = null;
  let loading = true;
  let error = null;
  let dirty = false;
  
  // Share state
  let showShare = false;
  let shareType: 'announcement' | 'report' = 'announcement';
  let isSubmitting = false;

  // When matchId changes, load the match data
  $: if (matchId) {
    loadMatchData(matchId);
  }

  async function loadMatchData(id: string) {
    loading = true;
    error = null;
    
    try {
      await getMatch(id, async (result, status) => {
        if (status === 200) {
          match = result;
          // Refresh settings in case backend switched team context
          await refreshSettings();
        } else {
          error = $_('match.errors.loadFailed');
          showToast(error, 'error');
        }
        loading = false;
      });
    } catch (err) {
      console.error('Error loading match data:', err);
      error = $_('match.errors.loadFailed');
      showToast(error, 'error');
      loading = false;
    }
  }
  
  // Save match data when necessary
  async function saveMatchData() {
    if (!match || !dirty) return;
    
    try {
      const result = await saveReportWithImage(match);
      match = result;
      dirty = false;
      
    } catch (err) {
      console.error('Error saving match data:', err);
      showToast($_('match.errors.saveFailed'), 'error');
    }
  }
  
  // Handle changes to match data
  async function handleMatchChange() {
    dirty = true;
    await saveMatchData();
    // THE FOLLOWING CAUSES ISSUES ON AUTOPEN FORM FOR SCORER ON NEW GOAL
    // await loadMatchData(matchId);
  }
  
  // Handle changes to highlights
  async function handleHighlightsChange(event) {
    if (!match || !match.id) return;
    
    try {
      const highlights = event.detail.highlights;
      await updateMatchHighlights(match.id.toString(), highlights, (result, status) => {
        if (status === 200) {
          // console.log('Highlights saved:', highlights);
          match.highlights = highlights;
          dirty = true;
          saveMatchData(); //needed to update the score in the match object, not really efficient! 
          // showToast($_('match.success.highlightsSaved'), 'success');
          trackEvent('update_highlights', {
            module: "scoreboard",
            action_type: "update_data",
            description: 'Update highlights',
          }, $settingsStore.user.id);
        } else {
          showToast($_('match.errors.highlightsSaveFailed'), 'error');
        }
      });
    } catch (err) {
      console.error('Error saving highlights:', err);
      showToast($_('match.errors.highlightsSaveFailed'), 'error');
    }
  }
  
  // Handle share action
  async function handleShare(type: 'announcement' | 'report') {
    try {
      isSubmitting = true;
      // Make sure data is saved before sharing
      await saveMatchData();
      shareType = type;
      showShare = true;
    } catch (error) {
      console.error('Error preparing share:', error);
      showToast($_('match.errors.shareFailed'), 'error');
    } finally {
      isSubmitting = false;
    }
  }

  function scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }

  // Track scroll position for header blur effect
  let isScrolled = false;

  function handleScroll() {
    isScrolled = window.scrollY > 10;
  }

  onMount(() => {
    // Ensure settings are loaded before rendering the modal
    if (!$settingsStore.active_team.id) {
      refreshSettings();
    }

    // Add scroll listener
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  });
</script>

{#if !showShare}
<div class="page-container">
  {#if loading}
    <article aria-busy="true">
      <p>{$_('match.loading')}</p>
    </article>
  {:else if error}
    <article>
      <header>
        <h2>{$_('match.errors.title')}</h2>
      </header>
      <p>{error}</p>
      <footer>
        <button class="outline" on:click={() => history.back()}>
          {$_('match.actions.back')}
        </button>
      </footer>
    </article>
  {:else if match}
      <div class="header" class:scrolled={isScrolled}>
        <div class="header-row full-width p-2 clickable" on:click={() => window.location.hash = '#/'}>
          <div class="flex items-center">
            <span class="text text-lg text-bold">&larr;</span> <span class="text text-sm text-bold mr-4">{$_('match.actions.back')}</span> 
          </div>
          <div class="flex items-center">
            <img src={match.opponent_club.logo_filename? `/img/clublogos/${match.opponent_club.logo_filename}?${import.meta.env.VITE_CACHE_VERSION}` : '/img/clublogos/default.png'} alt="{match.opponent_club.name}" class="team-logo-small ml-2">
            <span class="text text-sm text-bold ml-2">
              {match.opponent_club?.name.substring(0, 14)}
            </span>
            <span class="text text-sm text-bold ml-2">
              {formatDateShort(match.match_date)}
            </span>
          </div>
        </div>

         <!-- Tab navigation -->
        <nav class="tabs full-width tab-row">
          <ul>
            <li>
              <a role="button" href="{`#/match/${matchId}/details`}" on:click={scrollToTop} class:active={view == 'details'}>
                {$_('match.tabs.preparation')}
              </a>
            </li>
            <li>
              <a role="button" href="{`#/match/${matchId}/highlights`}" on:click={scrollToTop} class:active={view == 'highlights'}>
                {$_('match.tabs.highlights')}
              </a>
            </li>
            <li>
              <a role="button" href="{`#/match/${matchId}/report`}" on:click={scrollToTop} class:active={view == 'report'}>
                {$_('match.tabs.report')}
              </a>
            </li>
          </ul>
        </nav>
      </div>
      
      {#if view === 'details'}
        <MatchPreparation 
          bind:match={match} 
          sessionInfo={$settingsStore} 
          on:change={handleMatchChange}
          on:share={({ detail }) => handleShare(detail.type)}
        />
      {:else if view === 'highlights'}
        <MatchHighlights 
          bind:match={match} 
          sessionInfo={$settingsStore} 
          on:matchChange={handleMatchChange}
          on:highlightsChange={handleHighlightsChange}
        />
      {:else if view === 'report'}
        <MatchReport 
          bind:match={match} 
          sessionInfo={$settingsStore}
          on:change={handleMatchChange}
          on:share={({ detail }) => handleShare(detail.type)}
        />
      {:else}
        <p>{$_('match.errors.invalidView')}</p>
      {/if}
  {:else}
    <article>
      <p>{$_('match.errors.matchNotFound')}</p>
    </article>
  {/if}
</div>
{/if}

{#if showShare}
  <ShareView 
    sessionInfo={$settingsStore}
    bind:report={match} 
    type={shareType} 
    on:change={handleMatchChange}
    onClose={() => showShare = false} 
  />
{/if}

<style>

  .page-container {
    padding-top: 140px;
  }

  .score {
    display: flex;
  }

  .away-game {
    flex-direction: row-reverse;
  }

  .header {
    margin-bottom: 0;
    position: relative;
    padding: 0.2rem 0;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    color: inherit;
    background: transparent;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    padding: .5rem 1rem;
    z-index: 100;
    transition: background 0.3s ease, backdrop-filter 0.3s ease;
  }

  .header.scrolled {
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
  }

  :global([data-theme="dark"]) .header.scrolled {
    background: rgba(17, 24, 39, 0.7);
  }

  .header-row {
    display: flex;
    justify-content: space-between;
    background: transparent;
    align-items: center;
    color: var(--pico-primary-text);
    margin-bottom: 1rem;
  }


  .tab-row {
    display: flex;
    justify-content: flex-start;
    max-width: 768px;
  }
    
  .page-container {
    padding-bottom: 5rem; /* Add padding to account for fixed navigation */
  }
</style> 